import React from 'react';

import { Link } from '@suntex.core/nav';

import { Button } from '../Button/Button';
import { getClassName } from '../_utils';

import './ResourceList.scss';

export interface ResourceItem {
  title: string;
  subtitle?: string;
  href?: string;
  to?: string; // For React Router Link
  target?: string;
  rel?: string;
  onClick?: () => void;
  className?: string;
  tabIndex?: number;
  /**
   * Accessible name override for the link/button, used when several items
   * share the same visible title (e.g. "National", "State") so each has a
   * unique accessible name.
   */
  ariaLabel?: string;
}

export interface ResourceListProps {
  /**
   * The title to display at the top of the resource list
   */
  title?: string;
  /**
   * The heading level for the title (h4 or h6)
   * @default 'h4'
   */
  titleLevel?: 'h4' | 'h6';
  /**
   * Array of resource items to display
   */
  resources?: ResourceItem[];
  /**
   * The content to display inside the resource list (deprecated: use resources prop instead)
   * @deprecated
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

export function ResourceList({
  title,
  titleLevel = 'h4',
  resources,
  children,
  className,
}: ResourceListProps) {
  const containerClassName = getClassName('resource-list', [
    'suntex-resources-list',
    ...(className ? [className] : []),
  ]);

  const TitleComponent = titleLevel;

  const renderResourceItem = (resource: ResourceItem, index: number) => (
    <li key={index}>
      <div className="resource-item">
        <div className="resource-item__title">
          {resource.to ? (
            <Link
              to={resource.to}
              className={resource.className}
              tabIndex={resource.tabIndex}
              target={resource.target}
              rel={resource.rel}
              aria-label={resource.ariaLabel}
            >
              {resource.title}
            </Link>
          ) : resource.href ? (
            <a
              href={resource.href}
              className={resource.className}
              tabIndex={resource.tabIndex}
              target={resource.target}
              rel={resource.rel}
              aria-label={resource.ariaLabel}
            >
              {resource.title}
            </a>
          ) : resource.onClick ? (
            <Button
              intent="icon"
              onClick={resource.onClick}
              className={resource.className}
              tabIndex={resource.tabIndex}
              aria-label={resource.ariaLabel}
            >
              {resource.title}
            </Button>
          ) : (
            <span className={resource.className}>{resource.title}</span>
          )}
        </div>
        {resource.subtitle && (
          <span className="resources-item__subtitle">{resource.subtitle}</span>
        )}
      </div>
    </li>
  );

  return (
    <div className={containerClassName}>
      {title && (
        <TitleComponent className="suntex-resources-list__title">
          {title}
        </TitleComponent>
      )}
      {resources && resources.length > 0 ? (
        <ul className={`resources-list`}>
          {resources.map(renderResourceItem)}
        </ul>
      ) : children ? (
        <div className={'resources-list'}>{children}</div>
      ) : null}
    </div>
  );
}
